import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PageLoadingProvider } from './context/PageLoadingContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { isStoreHost, isDirectoryHost } from './config/storeHost';

// Importar Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';

// Pages
import Login from './pages/Login';
import Layout from './components/Layout';
import Categories from './pages/Categories';

// Lazy loading para las páginas protegidas
const Products = React.lazy(() => import('./pages/Products'));
const Sales = React.lazy(() => import('./pages/Sales'));
const Purchases = React.lazy(() => import('./pages/Purchases'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Capital = React.lazy(() => import('./pages/Capital'));
const Yapes = React.lazy(() => import('./pages/Yapes'));
const Users = React.lazy(() => import('./pages/Users'));
const Inventory = React.lazy(() => import('./pages/Inventory'));
const Kardex = React.lazy(() => import('./pages/Kardex'));
const Cash = React.lazy(() => import('./pages/Cash'));
const OnlineOrders = React.lazy(() => import('./pages/OnlineOrders'));
const Customers = React.lazy(() => import('./pages/Customers'));
const MyStore = React.lazy(() => import('./pages/MyStore'));
const Facturacion = React.lazy(() => import('./pages/Facturacion'));
const SuperAdmin = React.lazy(() => import('./pages/SuperAdmin'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));

// Tienda pública (sin auth)
const Store = React.lazy(() => import('./pages/store/Store'));
const OrderTracking = React.lazy(() => import('./pages/store/OrderTracking'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Directory = React.lazy(() => import('./pages/Directory'));

// Router mínimo para los subdominios de tienda ({slug}.tuplazita.com): solo lo público.
const StoreApp: React.FC = () => (
  <ToastProvider>
    <Router>
      <Routes>
        <Route path="/" element={
          <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
            <Store />
          </React.Suspense>
        } />
        <Route path="/pedido/:id" element={
          <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
            <OrderTracking />
          </React.Suspense>
        } />
        <Route path="/terminos" element={
          <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
            <Terms />
          </React.Suspense>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  </ToastProvider>
);

// Router del apex tuplazita.com: directorio central público.
const DirectoryApp: React.FC = () => (
  <Router>
    <Routes>
      <Route path="/" element={
        <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
          <Directory />
        </React.Suspense>
      } />
      <Route path="/terminos" element={
        <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
          <Terms />
        </React.Suspense>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Router>
);

const App: React.FC = () => {
  // Apex (tuplazita.com) → directorio central.
  if (isDirectoryHost()) return <DirectoryApp />;
  // Subdominio de tienda → solo catálogo público (sin auth/admin).
  if (isStoreHost()) return <StoreApp />;

  return (
    <AuthProvider>
      <MainRoutes />
    </AuthProvider>
  );
};

/** Landing según rol: admin cae en el dashboard, el resto en Ventas. */
const HomeRedirect: React.FC = () => {
  const { user } = useAuth();
  return <Navigate to={user?.role === 'administrador' ? '/inicio' : '/ventas'} replace />;
};

const MainRoutes: React.FC = () => {
  return (
    <>
      <PageLoadingProvider>
      <ToastProvider>
      <ConfirmProvider>
      <Router>
        <Routes>
          {/* Rutas públicas */}
          <Route path="/login" element={<Login />} />
          <Route path="/terminos" element={
            <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
              <Terms />
            </React.Suspense>
          } />
          {/* Preview local del directorio (en prod vive en el apex tuplazita.com) */}
          <Route path="/directorio" element={
            <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
              <Directory />
            </React.Suspense>
          } />
          <Route path="/tienda/:slug" element={
            <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
              <Store />
            </React.Suspense>
          } />
          <Route path="/tienda/:slug/pedido/:id" element={
            <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}>
              <OrderTracking />
            </React.Suspense>
          } />

          {/* Rutas protegidas */}
          <Route path="/" element={<Layout />}>
            <Route index element={<HomeRedirect />} />
            <Route
              path="inicio"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Dashboard />
                </React.Suspense>
              }
            />
            <Route
              path="productos"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Products />
                </React.Suspense>
              }
            />
            <Route
              path="ventas"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Sales />
                </React.Suspense>
              }
            />
            <Route
              path="compras"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Purchases />
                </React.Suspense>
              }
            />
            <Route
              path="reportes"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Reports />
                </React.Suspense>
              }
            />
            <Route
              path="capital"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Capital />
                </React.Suspense>
              }
            />
            <Route
              path="yapes"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Yapes />
                </React.Suspense>
              }
            />
            <Route
              path="categorias"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Categories />
                </React.Suspense>
              }
            />
            <Route
              path="usuarios"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Users />
                </React.Suspense>
              }
            />
            <Route
              path="inventario"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Inventory />
                </React.Suspense>
              }
            />
            <Route
              path="movimientos"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Kardex />
                </React.Suspense>
              }
            />
            <Route
              path="caja"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Cash />
                </React.Suspense>
              }
            />
            <Route
              path="pedidos"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <OnlineOrders />
                </React.Suspense>
              }
            />
            <Route
              path="clientes"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Customers />
                </React.Suspense>
              }
            />
            <Route
              path="mi-tienda"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <MyStore />
                </React.Suspense>
              }
            />
            <Route
              path="facturacion"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <Facturacion />
                </React.Suspense>
              }
            />
            <Route
              path="super-admin"
              element={
                <React.Suspense fallback={<div>Cargando...</div>}>
                  <SuperAdmin />
                </React.Suspense>
              }
            />
          </Route>

          {/* Ruta por defecto */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      </ConfirmProvider>
      </ToastProvider>
      </PageLoadingProvider>
    </>
  );
};

export default App;
